/* SectionQualityPanel — compact header strip above each generated section.
 * Shows:
 *   • BUY/HOLD/PASS verdict (if executive_summary and parseable)
 *   • Quantification density badge (numbers/500w + grade)
 *   • Freshness buckets (FRESH / STALE / OUTDATED pills)
 *
 * Data comes from GET /api/countries/{id}/sections/{section}/quality, lazy-loaded
 * after the section is expanded (piggybacks on intersection observer via the
 * parent's `isGenerated` gate).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Zap, Gauge, Radio, Loader2, AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_TONE = {
  'PURSUE':                 { bg: 'from-emerald-500 to-green-600', text: 'text-white',  ring: 'ring-emerald-400/40' },
  'PURSUE NOW':             { bg: 'from-emerald-500 to-green-600', text: 'text-white',  ring: 'ring-emerald-400/40' },
  'PURSUE WITH GUARDRAILS': { bg: 'from-amber-500 to-orange-600',  text: 'text-white',  ring: 'ring-amber-400/40' },
  'WATCH':                  { bg: 'from-slate-500 to-slate-700',   text: 'text-white',  ring: 'ring-slate-400/40' },
  'PASS':                   { bg: 'from-rose-500 to-red-700',      text: 'text-white',  ring: 'ring-rose-400/40' },
};

const GRADE_TONE = {
  Excellent: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  Strong:    'bg-teal-500/15    text-teal-700    border-teal-500/30',
  Adequate:  'bg-amber-500/15   text-amber-700   border-amber-500/30',
  Light:     'bg-orange-500/15  text-orange-700  border-orange-500/30',
  Thin:      'bg-rose-500/15    text-rose-700    border-rose-500/30',
  'n/a':     'bg-slate-500/10   text-slate-500   border-slate-500/20',
};

const CITATION_TONE = {
  PASS: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  WARN: 'bg-amber-500/15   text-amber-700   border-amber-500/30',
  FAIL: 'bg-rose-500/15    text-rose-700    border-rose-500/30',
};

export default function SectionQualityPanel({ countryId, sectionKey, darkMode = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/countries/${countryId}/sections/${sectionKey}/quality`, { timeout: 30000 });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Quality metrics unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [countryId, sectionKey]);

  if (loading) {
    return (
      <div className={`mb-3 flex items-center gap-2 text-[11px] ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Loading quality metrics…
      </div>
    );
  }
  if (err || !data) return null;

  const { verdict, density, freshness_buckets: fb, citations } = data;
  const hasVerdict = verdict && verdict.label;
  const hasDensity = density && density.words > 0;
  const hasFreshness = fb && (fb.FRESH + fb.STALE + fb.FATAL + fb.UNKNOWN) > 0;
  const hasCitations = citations && citations.total_citations > 0;

  if (!hasVerdict && !hasDensity && !hasFreshness && !hasCitations) return null;

  return (
    <div className="mb-4 space-y-2" data-testid={`quality-panel-${sectionKey}`}>
      {/* Verdict banner (executive summary only, when parseable) */}
      {hasVerdict && (
        <div
          data-testid={`verdict-banner-${sectionKey}`}
          className={`relative overflow-hidden rounded-xl p-4 ring-1 ${VERDICT_TONE[verdict.label]?.ring || 'ring-slate-300/40'} bg-gradient-to-r ${VERDICT_TONE[verdict.label]?.bg || 'from-slate-500 to-slate-700'} ${VERDICT_TONE[verdict.label]?.text || 'text-white'} shadow-lg`}
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[9.5px] font-black uppercase tracking-[0.2em] opacity-80">Investment Verdict</p>
                <p className="text-xl font-extrabold leading-none mt-0.5" data-testid="verdict-label">{verdict.label}</p>
                {verdict.top_risk && (
                  <p className="text-[11.5px] opacity-90 mt-1.5 max-w-xl"><span className="font-bold">Top risk:</span> {verdict.top_risk}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {verdict.score !== null && verdict.score !== undefined && (
                <div className="text-right">
                  <p className="text-[9px] font-black uppercase tracking-widest opacity-70">Score</p>
                  <p className="text-2xl font-extrabold leading-none" data-testid="verdict-score">{verdict.score}<span className="text-sm opacity-60">/100</span></p>
                </div>
              )}
              {verdict.horizon && (
                <div className="text-right">
                  <p className="text-[9px] font-black uppercase tracking-widest opacity-70">Horizon</p>
                  <p className="text-sm font-bold leading-tight mt-0.5">{verdict.horizon}</p>
                </div>
              )}
              {verdict.ticket && (
                <div className="text-right">
                  <p className="text-[9px] font-black uppercase tracking-widest opacity-70">Ticket</p>
                  <p className="text-sm font-bold leading-tight mt-0.5">{verdict.ticket}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Density + Freshness strip */}
      <div className="flex items-center gap-3 flex-wrap">
        {hasDensity && (
          <div
            data-testid={`density-meter-${sectionKey}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${GRADE_TONE[density.grade] || GRADE_TONE['n/a']} text-[11px]`}
            title={`${density.numbers} numeric facts across ${density.words} words · Target ≥${density.threshold}/500w`}
          >
            <Gauge className="w-3.5 h-3.5" />
            <span className="font-black">{density.per_500_words}</span>
            <span className="opacity-70">nums / 500w</span>
            <span className="mx-1 opacity-30">·</span>
            <span className="font-bold uppercase tracking-widest text-[9.5px]">{density.grade}</span>
            {density.pass
              ? <CheckCircle2 className="w-3 h-3 opacity-70" title="Meets PRD density bar (≥8/500w)" />
              : <AlertTriangle className="w-3 h-3 opacity-70" title="Below PRD density bar (≥8/500w)" />}
          </div>
        )}

        {hasFreshness && (
          <div
            data-testid={`freshness-strip-${sectionKey}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] ${darkMode ? 'bg-white/5 border-white/10 text-white/70' : 'bg-slate-50 border-slate-200 text-slate-700'}`}
          >
            <Radio className="w-3.5 h-3.5 text-emerald-500" />
            {fb.FRESH > 0 && <Pill color="emerald" n={fb.FRESH} label="FRESH" />}
            {fb.STALE > 0 && <Pill color="amber"   n={fb.STALE} label="STALE" />}
            {fb.FATAL > 0 && <Pill color="rose"    n={fb.FATAL} label="OUTDATED" />}
            <span className="opacity-50 text-[9.5px] uppercase tracking-widest">data sources</span>
          </div>
        )}

        {hasCitations && (
          <div
            data-testid={`citations-badge-${sectionKey}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] ${CITATION_TONE[citations.status]}`}
            title={`${citations.verified_api + citations.verified_source + citations.verified_canon}/${citations.total_citations} citations verified · ${Math.round(citations.verified_rate * 100)}%`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="font-black">{citations.verified_api + citations.verified_source + citations.verified_canon}</span>
            <span className="opacity-70">/</span>
            <span className="font-black">{citations.total_citations}</span>
            <span className="opacity-70">verified</span>
            <span className="mx-0.5 opacity-30">·</span>
            <span className="font-bold uppercase tracking-widest text-[9.5px]">{citations.status}</span>
            {citations.unverified > 0 && (
              <span className="text-[9.5px] font-black px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-700 border border-rose-500/30 ml-1">
                {citations.unverified} flagged
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ color, n, label }) {
  const map = {
    emerald: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
    amber:   'bg-amber-500/15   text-amber-700   border-amber-500/30',
    rose:    'bg-rose-500/15    text-rose-700    border-rose-500/30',
  };
  return (
    <span className={`text-[9.5px] font-black px-1.5 py-0.5 rounded-full border ${map[color]}`}>
      {n} {label}
    </span>
  );
}
